import React from 'react';
import { IDexTrade } from '@kattana-io/common';
import { useTradesTableData } from './useTradesTableData';
import { formatTimestamp } from '../../DexTrades/formatters';
import { formatWallet } from './formatters';
import Text from '../../../atoms/Text';
import ModalTable from '../ModalTable/BodyTable';
import { onTxScan } from '../../../helpers/addressUtils';
import NumberDisplay from '../../../molecules/NumberDisplay';

const formatTrade = (
  trade: IDexTrade,
  flipped: boolean,
  baseDecimals: number,
  quoteDecimals: number
) => {
  const { buy, amount0, amount1 } = trade;

  // if pair flipped - buy/sell will be reversed
  const isBuy = buy === 1;

  // get amounts for base and quote considering flipped
  const baseAmount = Number(amount0);
  const quoteAmount = Number(amount1);

  const bDecimals = flipped ? quoteDecimals : baseDecimals;
  const qDecimals = flipped ? baseDecimals : quoteDecimals;

  // get in and out amounts depending on trade was buy or sell
  const amountIn = isBuy
    ? baseAmount / 10 ** bDecimals
    : quoteAmount / 10 ** qDecimals;

  const amountOut = isBuy
    ? quoteAmount / 10 ** qDecimals
    : baseAmount / 10 ** bDecimals;

  return { amountIn, amountOut, isBuy };
};

export interface ITradesTableProps {
  trades: IDexTrade[];
  base: string;
  quote: string;
  base_decimals: number;
  quote_decimals: number;
  flipped: boolean;
  chain: string;
  timezone?: string;
}

const TradesTable = (props: ITradesTableProps) => {
  const { base, quote, trades, base_decimals, quote_decimals, flipped } = props;

  const { columns, data } = useTradesTableData({
    trades,
    base,
    quote,
    flipped,
  });

  const Cell = (cell: any, row: IDexTrade): React.ReactNode => {
    const { isBuy, amountIn, amountOut } = formatTrade(
      row,
      flipped,
      base_decimals,
      quote_decimals
    );
    const inSym = flipped ? (isBuy ? quote : base) : isBuy ? base : quote;
    const outSym = flipped ? (isBuy ? base : quote) : isBuy ? quote : base;

    switch (cell.column.id) {
    case 'date':
      return (
        <Text size={12}>{formatTimestamp(cell.value, props.timezone)}</Text>
      );
    case 'priceAUsd':
      return (
        <NumberDisplay
          value={cell.value}
          startSymbol='$'
          textProps={{
            size: 12,
            className: row.buy === 1 ? 'row__buy' : 'row__sell',
          }}
        />
      );
    case 'priceA':
      return (
        <NumberDisplay
          value={cell.value}
          startSymbol='('
          endSymbol=')'
          textProps={{ size: 12 }}
        />
      );
    case 'amountIn':
      return (
        <NumberDisplay
          value={amountIn}
          endSymbol={` ${inSym}`}
          textProps={{ size: 12 }}
        />
      );
    case 'amountOut':
      return (
        <NumberDisplay
          value={amountOut}
          endSymbol={` ${outSym}`}
          textProps={{ size: 12 }}
        />
      );
    case 'valueUsd':
      return (
        <NumberDisplay
          value={Number(cell.value)}
          startSymbol='$'
          textProps={{ size: 12 }}
        />
      );
    case 'tx':
      return (
        <Text
          size={12}
          onClick={() => onTxScan(cell.value, props.chain)}
          className='attach_right link__color'
        >
          {formatWallet(cell.value)}
        </Text>
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  return <ModalTable data={data} columns={columns} renderCell={Cell} />;
};

export default TradesTable;
